import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { formatCurrency, formatDisplayValue, formatPoints, formatSaleOrigin } from "../lib/display";
import { CalendarDays, Coins, FileText, Package, Receipt, RotateCcw, Store, UserRound, XCircle } from "lucide-react";
import SaleStatusBadge from "./SaleStatusBadge";

const getSaleItems = (venda) => {
  if (venda?.itens?.length) return venda.itens;
  if (venda?.produtos?.length) return venda.produtos;
  return [];
};

const getSaleCode = (venda) => (
  venda?.numero_documento ||
  venda?.id_venda_erp ||
  venda?.id?.slice(0, 8) ||
  "Venda"
);

const getItemName = (item) => (
  item?.produto ||
  item?.descricao_produto ||
  item?.descricao ||
  item?.sku ||
  item?.codigo_produto_erp ||
  "Produto"
);

const formatDateTime = (value) => {
  if (!value) return "Não informado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Não informado";

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="min-w-0 rounded-lg border border-slate-100 bg-slate-50 p-3">
    <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{value || "Não informado"}</p>
  </div>
);

const SaleDetailsDialog = ({
  open,
  onOpenChange,
  venda,
  showCustomerDocument = false,
  showSaleValues = true,
}) => {
  const items = getSaleItems(venda);
  const cliente = venda?.cliente_erp || {};
  const saleCode = getSaleCode(venda);
  const saleDate = venda?.data_venda || venda?.created_at;
  const pontosEstornados = Number(venda?.pontos_estornados || 0);
  const valorDevolvido = Number(venda?.valor_devolvido || 0);
  const devolucoes = Array.isArray(venda?.devolucoes) ? venda.devolucoes : [];
  const vendaCancelada = String(venda?.status_venda || "").toLowerCase().includes("cancel");
  const summaryGridClass = showSaleValues
    ? "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    : "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Detalhes da venda</DialogTitle>
          <DialogDescription>
            Documento {saleCode}
          </DialogDescription>
        </DialogHeader>

        {venda && (
          <div className="space-y-5">
            <div className={`grid grid-cols-1 gap-3 ${summaryGridClass}`}>
              {showSaleValues && (
                <DetailItem icon={Receipt} label="Valor" value={formatCurrency(venda.valor_total)} />
              )}
              <DetailItem icon={Coins} label="Pontos" value={`+${formatPoints(venda.pontos_gerados)}`} />
              {pontosEstornados > 0 && (
                <DetailItem icon={RotateCcw} label="Pontos estornados" value={`-${formatPoints(pontosEstornados)}`} />
              )}
              {showSaleValues && valorDevolvido > 0 && (
                <DetailItem
                  icon={RotateCcw}
                  label="Valor dos produtos devolvidos"
                  value={formatCurrency(valorDevolvido)}
                />
              )}
              <DetailItem icon={FileText} label="Origem" value={formatSaleOrigin(venda.origem)} />
              <DetailItem icon={Store} label="Loja da venda" value={venda.loja_nome} />
              <DetailItem icon={CalendarDays} label="Data" value={formatDateTime(saleDate)} />
            </div>

            {vendaCancelada && (
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800" role="status">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-sm font-semibold">Venda cancelada</p>
                  <p className="mt-1 text-sm text-red-700">Os pontos creditados por esta venda foram estornados.</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section className="min-w-0 rounded-lg border border-slate-100 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <UserRound className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-semibold text-slate-900">Cliente</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="break-words">
                    <span className="text-slate-500">Nome: </span>
                    <span className="font-medium text-slate-900">{cliente.nome_cliente || "Não informado"}</span>
                  </p>
                  <p className="break-words">
                    <span className="text-slate-500">Código: </span>
                    <span className="font-medium text-slate-900">{cliente.id_cliente_erp || "Não informado"}</span>
                  </p>
                  {showCustomerDocument && (
                    <p className="break-words">
                      <span className="text-slate-500">CPF/CNPJ: </span>
                      <span className="font-medium text-slate-900">{cliente.cpf_cnpj_cliente || "Não informado"}</span>
                    </p>
                  )}
                </div>
              </section>

              <section className="min-w-0 rounded-lg border border-slate-100 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-semibold text-slate-900">Documento</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p className="break-words">
                    <span className="text-slate-500">Número: </span>
                    <span className="font-medium text-slate-900">{venda.numero_documento || "Não informado"}</span>
                  </p>
                  <p className="break-words">
                    <span className="text-slate-500">Código ERP: </span>
                    <span className="font-medium text-slate-900">{venda.id_venda_erp || "Não informado"}</span>
                  </p>
                  <p className="break-words">
                    <span className="text-slate-500">Operação: </span>
                    <span className="font-medium text-slate-900">{formatDisplayValue(venda.tipo_operacao)}</span>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-slate-500">Status:</span>
                    <SaleStatusBadge status={venda.status_venda || "Faturado"} />
                  </div>
                  {devolucoes.length > 0 && (
                    <p className="break-words">
                      <span className="text-slate-500">Devoluções: </span>
                      <span className="font-medium text-slate-900">
                        {devolucoes
                          .map((devolucao) => devolucao?.numero_documento)
                          .filter(Boolean)
                          .join(", ") || "Registrada no ERP"}
                      </span>
                    </p>
                  )}
                </div>
              </section>
            </div>

            <section className="min-w-0 rounded-lg border border-slate-100 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-primary" />
                  <h3 className="font-heading font-semibold text-slate-900">Produtos</h3>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                  {items.length} {items.length === 1 ? "item" : "itens"}
                </span>
              </div>

              {items.length > 0 ? (
                <div className="space-y-3">
                  {items.map((item, index) => {
                    const quantidade = item.quantidade_item ?? item.quantidade;
                    const quantidadeDevolvida = Number(item.quantidade_devolvida || 0);
                    const valorUnitario = item.valor_unitario_item ?? item.valor_unitario;
                    const valorTotal = item.valor_total_item ?? item.valor;

                    return (
                      <div key={`${item.codigo_produto_erp || item.sku || index}-${index}`} className="min-w-0 rounded-lg bg-slate-50 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="break-words text-sm font-semibold text-slate-900">
                              {getItemName(item)}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                              {item.sku && <span>SKU {item.sku}</span>}
                              {item.codigo_produto_erp && <span>Cód. {item.codigo_produto_erp}</span>}
                              {item.nome_marca && <span>{item.nome_marca}</span>}
                            </div>
                          </div>
                          {showSaleValues && (
                            <span className="shrink-0 text-sm font-bold text-slate-900">
                              {formatCurrency(valorTotal)}
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-600">
                          <span>Qtd: <strong>{quantidade ?? "Não informado"}</strong></span>
                          {quantidadeDevolvida > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 font-medium text-amber-800">
                              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                              {quantidadeDevolvida} {quantidadeDevolvida === 1 ? "devolvido" : "devolvidos"}
                            </span>
                          )}
                          {showSaleValues && (
                            <span>Unitário: <strong>{formatCurrency(valorUnitario, "Não informado")}</strong></span>
                          )}
                          <span>Grupo: <strong>{item.codigo_grupo_produto || item.categoria || "Não informado"}</strong></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Nenhum produto informado para esta venda.
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SaleDetailsDialog;
